// Ficha de un disco (Req. 2.3, 2.4, 2.6, 2.9, 6.3).
// Orden de secciones: hero (carátula + identidad) → ID de disco (grande) →
// Ficha Técnica → Estado Físico (con fundas/carátula/limpieza) → Valor del Disco
// (valor real + mercado Discogs) → Comentarios (personales + Discogs) →
// Tracklist → Discogs Live Sync → Historial de Auditoría (agrupado por operación,
// expandible, con fechas de catalogado/actualizado).

import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AsyncBoundary, GoldmineBadge, Icon } from '../ui/components.js';
import { RecordSyncPanel } from './RecordSyncPanel.js';
import { RecordChangesPanel } from './RecordChangesPanel.js';
import type { Record as VinylRecord } from '../types.js';
import type { CustomFieldDef } from '../api/endpoints.js';

export function RecordDetailView() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // El tracklist llega EMBEBIDO en `GET /records/{id}` (campo `tracklist` de la
  // ficha), así que no se hace una segunda llamada: se lee de `r.tracklist`.
  const record = useAsync<VinylRecord>(() => api.records.get(id), [id]);

  // Contador que fuerza el refresco del historial de auditoría cuando se aplica
  // una sincronización (o cualquier operación que genere cambios). El panel de
  // historial carga sus datos aparte de la ficha, así que sin esta señal solo se
  // actualizaría al recargar la página.
  const [syncVersion, setSyncVersion] = useState(0);

  /**
   * Vuelve a los resultados por donde se accedió. Usa el historial del
   * navegador (preserva búsqueda/orden/scroll del catálogo); si no hay historial
   * previo (p. ej. entrada directa por URL), cae al catálogo.
   */
  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }

  return (
    <AsyncBoundary state={record}>
      {(r) => (
        <div className="flex flex-col">
          {/* Barra superior: volver a los resultados + editar */}
          <div className="sticky top-0 z-20 px-margin-mobile py-space-sm bg-surface-base/80 backdrop-blur border-b border-border-subtle flex items-center justify-between gap-space-sm">
            <button
              type="button"
              onClick={goBack}
              className="flex items-center gap-space-xs text-primary font-body-md text-body-md"
            >
              <Icon name="arrow_back" className="text-[20px]" />
              {t('recordDetail.back')}
            </button>
            {hasPermission('catalog', 'read_write') && (
              <button
                type="button"
                onClick={() => navigate(`/records/${r.id}/edit`)}
                className="flex items-center gap-space-xs px-3 h-9 rounded-lg bg-surface-rack border border-primary-container/40 text-primary font-body-sm text-body-sm"
              >
                <Icon name="edit" className="text-[18px]" /> {t('recordDetail.edit')}
              </button>
            )}
          </div>

          {/* Hero: carátula + identidad */}
          <div className="px-margin-mobile pt-space-md flex flex-col gap-space-md">
            <div className="relative rounded-xl overflow-hidden border border-border-subtle bg-surface-container-high aspect-square flex items-center justify-center">
              {r.cover_image_url ? (
                <img
                  src={r.cover_image_url}
                  alt={t('recordDetail.coverAlt', { title: r.title })}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback al icono si la imagen falla: oculta el <img> roto.
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <Icon name="album" className="text-on-surface-variant text-[64px] opacity-40" />
              )}
              {r.original_release_year && (
                <span className="absolute bottom-space-sm left-space-sm px-2 py-1 rounded bg-surface-base/80 backdrop-blur text-vacuum-glow font-badge-micro text-badge-micro uppercase tracking-wider">
                  {r.original_release_year === r.edition_year ? t('recordDetail.firstPressing') : t('recordDetail.reissue')} {r.edition_year ?? ''}
                </span>
              )}
            </div>

            <div className="flex items-start justify-between gap-space-sm">
              <div className="min-w-0">
                <h1 className="font-headline-lg-mobile text-headline-lg-mobile font-bold tracking-tight text-on-surface">
                  {r.title}
                </h1>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  {r.artist_name ?? t('recordDetail.noArtist')} · {r.edition_year ?? t('recordDetail.noYear')}
                  {r.origin_country ? ` · ${r.origin_country}` : ''}
                </p>
              </div>
              {r.label_catalog_nr && (
                <span className="shrink-0 font-code-catalog text-body-sm text-primary bg-surface-rack border border-border-subtle rounded px-2 py-1">
                  {r.label_catalog_nr}
                </span>
              )}
            </div>

            {/* Chips de estado (sin ID: el ID se muestra grande abajo) */}
            <div className="flex flex-wrap items-center gap-space-xs">
              {r.copies > 1 && (
                <span className="flex items-center gap-1 px-2 py-1 rounded bg-surface-rack border border-border-subtle font-code-catalog text-body-sm text-on-surface">
                  <Icon name="content_copy" className="text-[14px] text-tertiary" />{t('recordDetail.copies', { count: r.copies })}
                </span>
              )}
              {r.needs_review && (
                <span className="flex items-center gap-1 px-2 py-1 rounded bg-priority-urgent/15 border border-priority-urgent/40 font-badge-micro text-badge-micro uppercase text-priority-urgent">
                  <Icon name="flag" className="text-[14px]" />{t('recordDetail.needsReview')}
                </span>
              )}
            </div>
          </div>

          {/* ID interno + Ubicación física — en fila propia tras la carátula */}
          {(r.internal_cat_num != null || r.location) && (
            <div className="px-margin-mobile pt-space-md">
              <div className="surface-card flex items-start gap-space-lg">
                {r.internal_cat_num != null && (
                  <div className="flex flex-col">
                    <span className="section-label text-primary/80">{t('recordDetail.recordId')}</span>
                    <span className="font-code-catalog text-[26px] font-bold text-primary leading-none">
                      #{r.internal_cat_num}
                    </span>
                  </div>
                )}
                <div className="flex flex-col min-w-0">
                  <span className="section-label text-primary/80">{t('recordDetail.location')}</span>
                  <span className="font-code-catalog text-[26px] font-bold text-primary leading-none break-words">
                    {locationLabel(r.location)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Ficha técnica (orden solicitado) — no colapsable, siempre visible */}
          <div className="px-margin-mobile pt-space-md">
            <section className="surface-card">
              <SectionTitle icon="settings" label={t('recordDetail.techSheet')} />
              <div className="mt-space-md grid grid-cols-2 gap-space-md">
              {/* 1: Género · Subgénero */}
              <Detail t={t} label={t('recordDetail.genre')} value={r.genre_name} />
              <Detail t={t} label={t('recordDetail.subgenre')} value={r.subgenre} />
              {/* 2: Género Discogs · Estilo Discogs */}
              <Detail t={t} label={t('recordDetail.discogsGenre')} value={r.discogs_genre} />
              <Detail t={t} label={t('recordDetail.discogsStyles')} value={r.discogs_styles} />
              {/* 3: Año release (original) · Año edición */}
              <Detail t={t} label={t('recordDetail.originalReleaseYear')} value={r.original_release_year} />
              <Detail t={t} label={t('recordDetail.editionYear')} value={r.edition_year} />
              {/* 4: Sello · Catalog # */}
              <Detail t={t} label={t('recordDetail.label')} value={r.label_name} />
              <Detail t={t} label={t('recordDetail.catalogNr')} value={r.label_catalog_nr} mono />
              {/* 5: País de origen · Barcode */}
              <Detail t={t} label={t('recordDetail.originCountry')} value={r.origin_country} />
              <Detail t={t} label={t('recordDetail.barcode')} value={r.barcode} mono />
              {/* 6: Formato · Tamaño del disco */}
              <Detail t={t} label={t('recordDetail.format')} value={r.discogs_format ?? r.type} />
              <Detail t={t} label={t('recordDetail.size')} value={r.size_inches ? `${r.size_inches}"` : null} />
              {/* 7: Color · Gramaje */}
              <Detail t={t} label={t('recordDetail.color')} value={r.vinyl_color} />
              <Detail t={t} label={t('recordDetail.grams')} value={r.grams ? `${r.grams} g` : null} />
              {/* 8: Discogs Master Release · Discogs Edit Release */}
              <Detail
                t={t}
                label={t('recordDetail.discogsMaster')}
                value={r.discogs_master_id}
                mono
                href={r.discogs_master_id != null ? `https://www.discogs.com/master/${r.discogs_master_id}` : undefined}
              />
              <Detail
                t={t}
                label={t('recordDetail.discogsRelease')}
                value={r.discogs_release_id}
                mono
                href={r.discogs_release_id != null ? `https://www.discogs.com/release/${r.discogs_release_id}` : undefined}
              />
              {/* 9: Vinilos dentro del álbum · Ejemplares que poseo */}
              <Detail t={t} label={t('recordDetail.discsPerAlbum')} value={r.discs_per_album} />
              <Detail t={t} label={t('recordDetail.copiesOwned')} value={r.copies} />
            </div>
            </section>
          </div>

          {/* Estado físico Goldmine + fundas/carátula/limpieza */}
          <CollapsibleSection icon="verified" label={t('recordDetail.physicalCondition')}>
            <div className="grid grid-cols-2 gap-space-sm">
              <div className="surface-rack-card !p-space-sm flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="section-label text-primary/80">{t('recordDetail.media')}</span>
                  <span className="font-body-md text-body-md font-bold text-on-surface">
                    {gradeName(t, r.media_condition)}
                  </span>
                </div>
                {r.media_condition && <GoldmineBadge grade={r.media_condition} />}
              </div>
              <div className="surface-rack-card !p-space-sm flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="section-label text-primary/80">{t('recordDetail.sleeveOuter')}</span>
                  <span className="font-body-md text-body-md font-bold text-on-surface">
                    {gradeName(t, r.sleeve_condition)}
                  </span>
                </div>
                {r.sleeve_condition && <GoldmineBadge grade={r.sleeve_condition} />}
              </div>
            </div>
            {/* Sleeve, Original Cover, Last Clean */}
            <div className="mt-space-md grid grid-cols-2 gap-space-md">
              <Detail t={t} label={t('recordDetail.innerSleeve')} value={boolLabel(t, r.has_sleeve)} />
              <Detail t={t} label={t('recordDetail.originalCover')} value={boolLabel(t, r.original_cover)} />
              <Detail t={t} label={t('recordDetail.lastClean')} value={formatDateOrDash(r.last_cleaned)} />
            </div>
          </CollapsibleSection>

          {/* Valor del disco: valor real ingresado + 3 valores de Discogs */}
          <CollapsibleSection icon="payments" label={t('recordDetail.value')}>
            <div className="surface-rack-card !p-space-sm flex items-center justify-between">
              <span className="section-label text-primary/80">{t('recordDetail.realValue')}</span>
              <span className="font-code-catalog text-headline-sm font-bold text-vacuum-glow">
                {r.commercial_value != null ? `$${r.commercial_value}` : '—'}
              </span>
            </div>
            <div className="mt-space-md">
              <span className="section-label text-primary/80">{t('recordDetail.discogsMarket')}</span>
              <div className="grid grid-cols-3 gap-space-sm mt-space-xs">
                <PriceCell label={t('recordDetail.priceLow')} value={r.discogs_price_low} currency={r.discogs_price_currency} tone="text-on-surface-variant" />
                <PriceCell label={t('recordDetail.priceMid')} value={r.discogs_price_mid} currency={r.discogs_price_currency} tone="text-primary" />
                <PriceCell label={t('recordDetail.priceHigh')} value={r.discogs_price_high} currency={r.discogs_price_currency} tone="text-vacuum-glow" />
              </div>
              {r.discogs_price_updated_at != null && (
                <p className="text-label-sm text-on-surface-variant mt-space-xs">
                  {t('recordDetail.updatedAt', { date: formatDate(r.discogs_price_updated_at) })}
                </p>
              )}
            </div>
          </CollapsibleSection>

          {/* Comentarios: personales + Discogs */}
          <CollapsibleSection icon="edit_note" label={t('recordDetail.comments')}>
            {!r.comments && !r.discogs_notes && (
              <p className="font-body-sm text-body-sm text-on-surface-variant text-center py-space-sm">
                {t('recordDetail.noComments')}
              </p>
            )}
            {r.comments && (
              <div className="surface-rack-card !p-space-sm flex flex-col gap-space-xs">
                <span className="section-label text-primary/80">{t('recordDetail.personalComments')}</span>
                <p className="font-body-sm text-body-sm text-on-surface leading-relaxed whitespace-pre-wrap">{r.comments}</p>
              </div>
            )}
            {r.discogs_notes && (
              <div className="surface-rack-card !p-space-sm mt-space-sm flex flex-col gap-space-xs">
                <span className="section-label text-primary/80">{t('recordDetail.discogsNotes')}</span>
                <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed whitespace-pre-wrap">{r.discogs_notes}</p>
              </div>
            )}
          </CollapsibleSection>

          {/* Campos personalizados del tenant (Req. campos configurables) */}
          <CustomFieldsDetail record={r} />

          {/* Tracklist (embebido en la ficha) */}
          <CollapsibleSection icon="queue_music" label={t('recordDetail.tracklist')}>
            <div>
              {!r.tracklist || r.tracklist.length === 0 ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant text-center py-space-sm">
                  {t('recordDetail.noTracklist')}
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-border-subtle">
                  {r.tracklist.map((track, i) => (
                    <li key={track.id ?? i} className="flex items-center gap-space-sm py-space-sm">
                      <span className="w-8 shrink-0 font-code-catalog text-body-sm text-primary font-bold">
                        {track.position ?? '—'}
                      </span>
                      <span className="flex-1 min-w-0 font-body-md text-body-md text-on-surface truncate">
                        {track.title}
                      </span>
                      {track.artist_top_rank != null && (
                        <span
                          className="shrink-0 inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 font-code-catalog text-label-sm text-primary font-bold"
                          title={t('recordDetail.topRankTitle', { rank: track.artist_top_rank })}
                        >
                          <Icon name="star" className="text-[14px] leading-none" />
                          {t('recordDetail.topRank', { rank: track.artist_top_rank })}
                        </span>
                      )}
                      {track.duration && (
                        <span className="font-code-catalog text-body-sm text-on-surface-variant">{track.duration}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CollapsibleSection>

          {/* Discogs Sync + Historial (paneles con lógica propia) */}
          <div className="px-margin-mobile py-space-lg flex flex-col gap-space-md">
            {hasPermission('sync', 'read_write') && (
              <RecordSyncPanel
                recordId={r.id}
                onApplied={() => {
                  record.reload();
                  setSyncVersion((v) => v + 1);
                }}
              />
            )}
            {hasPermission('catalog', 'read') && (
              <RecordChangesPanel
                recordId={r.id}
                canRevert={hasPermission('sync', 'read_write')}
                cataloguedAt={r.catalogued_at}
                updatedAt={r.updated_at}
                refreshKey={syncVersion}
              />
            )}
          </div>
        </div>
      )}
    </AsyncBoundary>
  );
}

/**
 * Sección de CAMPOS PERSONALIZADOS del tenant (Req. campos configurables).
 * Carga las definiciones de campos personalizados (para conocer etiquetas y
 * tipos) y muestra el valor guardado de cada uno en `record.custom_fields`.
 * No renderiza nada si el tenant no ha definido campos personalizados.
 */
function CustomFieldsDetail({ record }: { record: VinylRecord }) {
  const { t } = useTranslation();
  const api = useApi();
  const config = useAsync(() => api.config.getRecordFields(), []);
  const defs = config.data?.customFields ?? [];
  const values = (record.custom_fields ?? {}) as { [key: string]: unknown };

  // Sin definiciones (aún cargando o el tenant no configuró ninguno) → nada.
  if (defs.length === 0) return null;

  return (
    <CollapsibleSection icon="dynamic_form" label={t('recordDetail.customFields')}>
      <div className="grid grid-cols-2 gap-space-md">
        {defs.map((def) => (
          <Detail key={def.key} t={t} label={def.label} value={customValueLabel(t, def, values[def.key])} />
        ))}
      </div>
    </CollapsibleSection>
  );
}

/** Formatea el valor de un campo personalizado para mostrarlo en la ficha. */
function customValueLabel(t: TFunction, def: CustomFieldDef, value: unknown): string | number | null {
  if (value === null || value === undefined || value === '') return null;
  if (def.type === 'boolean') {
    if (value === true || value === 'true') return t('recordDetail.yes');
    if (value === false || value === 'false') return t('recordDetail.no');
    return null;
  }
  if (def.type === 'date') return typeof value === 'string' ? formatDateOrDash(value) : String(value);
  if (def.type === 'number') return typeof value === 'number' ? value : String(value);
  return String(value);
}

function SectionTitle({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex items-center gap-space-xs">
      <Icon name={icon} className="text-primary text-[20px]" />
      <h2 className="font-headline-sm text-headline-sm text-on-surface uppercase tracking-wider">{label}</h2>
    </div>
  );
}

/**
 * Sección colapsable de la ficha, presentada como PANEL con borde exterior
 * (mismo tratamiento `surface-card` que el resto de secciones) para que todas se
 * diferencien de forma consistente. La cabecera (icono + título + chevron)
 * alterna la visibilidad del contenido. Arranca expandida salvo `defaultOpen=false`.
 */
function CollapsibleSection({
  icon,
  label,
  defaultOpen = true,
  children,
}: {
  icon: string;
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="px-margin-mobile pt-space-md">
      <section className="surface-card">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-space-xs"
          aria-expanded={open}
        >
          <SectionTitle icon={icon} label={label} />
          <Icon
            name="expand_more"
            className={`text-on-surface-variant text-[22px] transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {open && <div className="mt-space-md">{children}</div>}
      </section>
    </div>
  );
}

function Detail({
  t,
  label,
  value,
  mono,
  href,
}: {
  t: TFunction;
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
  /** Si se provee (y hay valor), el valor se muestra como enlace que abre en otra pestaña. */
  href?: string;
}) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="flex flex-col gap-space-xs">
      <dt className="section-label text-primary/80">{label}</dt>
      <dd className={`m-0 text-body-md font-bold text-on-surface ${mono ? 'font-code-catalog' : 'font-body-md'}`}>
        {empty ? (
          <span className="font-normal text-on-surface-variant">—</span>
        ) : href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-space-xs text-on-surface hover:underline"
            title={t('recordDetail.openInDiscogs', { label })}
          >
            {String(value)}
            <Icon name="open_in_new" className="text-[16px]" />
          </a>
        ) : (
          String(value)
        )}
      </dd>
    </div>
  );
}

function PriceCell({
  label,
  value,
  currency,
  tone,
}: {
  label: string;
  value: number | null | undefined;
  currency: string | null;
  tone: string;
}) {
  return (
    <div className="surface-card flex flex-col items-center gap-0.5 !p-space-sm">
      <span className="section-label text-primary/80">{label}</span>
      <span className={`font-code-catalog text-headline-sm font-bold ${tone}`}>
        {value != null ? `${currency === 'USD' || !currency ? '$' : ''}${value}` : '—'}
      </span>
    </div>
  );
}

/** Nombre legible del grado Goldmine. */
function gradeName(t: TFunction, grade: string | null): string {
  if (!grade) return '—';
  // Las etiquetas Goldmine se traducen vía recordDetail.grade.{grade}; si no
  // existe una entrada, se devuelve el código tal cual.
  const label = t(`recordDetail.grade.${grade}`, { defaultValue: grade });
  return label;
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/** Fecha legible o guion si no hay valor (para campos opcionales de fecha). */
function formatDateOrDash(value?: string | null): string {
  return value ? formatDate(value) : '—';
}

/** Etiqueta Sí/No/— para un booleano opcional (fundas, carátula original). */
function boolLabel(t: TFunction, value?: boolean | null): string {
  if (value === true) return t('recordDetail.yes');
  if (value === false) return t('recordDetail.no');
  return '—';
}

/**
 * Etiqueta legible de la ubicación física: sección (género) y subsección
 * (subgénero) del catálogo donde vive el vinilo, con el lugar físico si existe.
 * P. ej. "Hip-Hop / West Coast" o "Rock · Caja 3".
 */
function locationLabel(loc?: import('../types.js').Location | null): string {
  if (!loc) return '—';
  const section = [loc.genre_label, loc.subgenre_label]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .join(' / ');
  const place = loc.physical_place && loc.physical_place.trim() !== '' ? loc.physical_place.trim() : null;
  if (section && place) return `${section} · ${place}`;
  return section || place || '—';
}
